import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { CalendarClock, CreditCard, FileText, Loader2, ScrollText } from "lucide-react";
import * as React from "react";

import type { AdminOrgBillingConfigPatch, OrgCollectionMethod } from "@ando/core";
import { Badge, DateTimePicker, RadioGroup, RadioGroupItem, cn, useToast } from "@ando/ui";

import {
  useAdminOrgBillingConfig,
  useUpdateAdminOrgBillingConfig,
} from "@/features/admin/hooks/useAdminOrg";

/**
 * OrgBillingConfigCard — HOW this org is billed.
 *
 * Ando runs two commercial models and an org sits in exactly one of them:
 *
 *   · **Ando engine**   — per token: consumption × markup, collected on Stripe
 *     (`charge_automatically` with a card on file, or `send_invoice`).
 *   · **ando-platform** — per licence + seat: `license_contract`. The org is
 *     never billed per token; the SRL invoices it through SDI.
 *
 * On top of that sits NO-PAY mode (`billing_starts_at`): until that instant the
 * org is free — no usage invoice AND no seat charge — whatever the collection
 * method says. That precedence is why the no-pay row is rendered first and why
 * the method row is visibly muted while no-pay is active: showing an active
 * collection method next to an org nobody is charging would be a lie.
 *
 * Every control writes through ONE mutation and the server returns the
 * re-resolved config, so `effective_collection_method` / `is_no_pay` are never
 * recomputed client-side (the inherit-else-default rule lives in one place).
 */

/** The radio value standing in for "no override — inherit the default". */
const INHERIT = "__inherit__" as const;

type MethodChoice = OrgCollectionMethod | typeof INHERIT;

interface MethodOption {
  readonly value: MethodChoice;
  readonly icon: React.ComponentType<{ size?: number; className?: string }>;
  readonly label: ReturnType<typeof msg>;
  readonly hint: ReturnType<typeof msg>;
}

/**
 * Declared once, rendered in a loop. Adding a fourth collection method is a
 * single entry here — no new markup, no second place to keep in step.
 */
const METHOD_OPTIONS: readonly MethodOption[] = [
  {
    value: "charge_automatically",
    icon: CreditCard,
    label: msg`Carta salvata`,
    hint: msg`Stripe addebita automaticamente il metodo di pagamento salvato.`,
  },
  {
    value: "send_invoice",
    icon: FileText,
    label: msg`Fattura Stripe`,
    hint: msg`Stripe invia una fattura pagabile con link. Funziona senza carta salvata.`,
  },
  {
    value: "license_contract",
    icon: ScrollText,
    label: msg`Licenze e utenti`,
    hint: msg`Contratto ando-platform: mai fatturato a consumo. La fattura la emette la SRL.`,
  },
];

export interface OrgBillingConfigCardProps {
  readonly orgId: string;
}

export function OrgBillingConfigCard({ orgId }: OrgBillingConfigCardProps) {
  const { i18n } = useLingui();
  const { toast } = useToast();
  const { data: config, isLoading } = useAdminOrgBillingConfig(orgId);
  const update = useUpdateAdminOrgBillingConfig(orgId);

  // Local draft for the free-text price only. The radio and the date commit
  // immediately; a number field that saved on every keystroke would fire a
  // request per digit.
  const [priceDraft, setPriceDraft] = React.useState<string>("");
  React.useEffect(() => {
    setPriceDraft(
      config?.price_per_user_eur_override == null ? "" : String(config.price_per_user_eur_override),
    );
  }, [config?.price_per_user_eur_override]);

  const patch = React.useCallback(
    (body: AdminOrgBillingConfigPatch) => {
      update.mutate(body, {
        onError: () =>
          toast.error(i18n._(msg`Modifica non salvata`), {
            description: i18n._(msg`Riprova fra un momento.`),
          }),
      });
    },
    [update, toast, i18n],
  );

  if (isLoading || !config) {
    return (
      <section className="rounded-lg border border-hairline bg-card/55 p-4">
        <Loader2 size={14} className="animate-spin text-ink-3" aria-hidden />
        <span className="sr-only">{i18n._(msg`Caricamento configurazione`)}</span>
      </section>
    );
  }

  const noPay = config.is_no_pay;
  const startsAt = config.billing_starts_at ? new Date(config.billing_starts_at) : null;
  const methodValue: MethodChoice = config.collection_method ?? INHERIT;
  const defaultLabel = METHOD_OPTIONS.find(
    (o) => o.value === config.default_collection_method,
  )?.label;

  const commitPrice = () => {
    const trimmed = priceDraft.trim();
    if (trimmed === "") {
      if (config.price_per_user_eur_override != null) {
        patch({ price_per_user_eur_override: null });
      }
      return;
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || parsed < 0) {
      // Reject rather than send garbage; restore what the server holds.
      setPriceDraft(
        config.price_per_user_eur_override == null
          ? ""
          : String(config.price_per_user_eur_override),
      );
      toast.error(i18n._(msg`Prezzo non valido`), {
        description: i18n._(msg`Inserisci un importo maggiore o uguale a zero.`),
      });
      return;
    }
    if (parsed !== config.price_per_user_eur_override) {
      patch({ price_per_user_eur_override: parsed });
    }
  };

  return (
    <section
      aria-labelledby="org-billing-config"
      className="flex flex-col gap-4 rounded-lg border border-hairline bg-card/55 p-4"
    >
      <div className="flex items-center justify-between gap-3">
        <h3 id="org-billing-config" className="font-medium text-[13px] text-ink-1">
          <Trans>Modalità di fatturazione</Trans>
        </h3>
        <Badge variant={noPay ? "secondary" : "default"}>
          {noPay ? i18n._(msg`Non fatturata`) : i18n._(msg`Fatturazione attiva`)}
        </Badge>
      </div>

      {/* NO-PAY — first, because it overrides everything below it. */}
      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="org-billing-starts-at"
          className="flex items-center gap-1.5 font-medium text-[12px] text-ink-2"
        >
          <CalendarClock size={13} className="text-accent" aria-hidden />
          <Trans>Inizio fatturazione</Trans>
        </label>
        <DateTimePicker
          id="org-billing-starts-at"
          value={startsAt}
          onChange={(next) => patch({ billing_starts_at: next ? next.toISOString() : null })}
          withTime
          disabled={update.isPending}
          placeholder={i18n._(msg`Nessuna data — org gratuita`)}
        />
        <p className="text-[11.5px] text-ink-3">
          {noPay ? (
            <Trans>
              Finché questa data è vuota o futura l'organizzazione non paga nulla: nessuna fattura a
              consumo e nessun addebito per le licenze.
            </Trans>
          ) : (
            <Trans>La fatturazione è attiva da questa data.</Trans>
          )}
        </p>
      </div>

      {/* Collection method. Muted while no-pay, because it is not in effect. */}
      <fieldset
        className={cn("flex flex-col gap-1.5", noPay && "opacity-60")}
        disabled={update.isPending}
      >
        <legend className="font-medium text-[12px] text-ink-2">
          <Trans>Come paga</Trans>
        </legend>
        <RadioGroup
          value={methodValue}
          onValueChange={(next) =>
            patch({
              collection_method: next === INHERIT ? null : (next as OrgCollectionMethod),
            })
          }
          className="flex flex-col gap-1.5"
        >
          <label
            htmlFor="method-inherit"
            className="flex cursor-pointer items-start gap-2.5 rounded-md p-1.5 hover:bg-card"
          >
            <RadioGroupItem value={INHERIT} id="method-inherit" className="mt-0.5" />
            <span className="flex flex-col">
              <span className="text-[12.5px] text-ink-1">
                <Trans>Predefinito della piattaforma</Trans>
              </span>
              <span className="text-[11.5px] text-ink-3">
                {defaultLabel
                  ? i18n._(msg`Attualmente: ${i18n._(defaultLabel)}`)
                  : i18n._(msg`Eredita l'impostazione globale.`)}
              </span>
            </span>
          </label>

          {METHOD_OPTIONS.map(({ value, icon: Icon, label, hint }) => (
            <label
              key={value}
              htmlFor={`method-${value}`}
              className="flex cursor-pointer items-start gap-2.5 rounded-md p-1.5 hover:bg-card"
            >
              <RadioGroupItem value={value} id={`method-${value}`} className="mt-0.5" />
              <span className="flex flex-col">
                <span className="flex items-center gap-1.5 text-[12.5px] text-ink-1">
                  <Icon size={13} className="text-ink-3" aria-hidden />
                  {i18n._(label)}
                </span>
                <span className="text-[11.5px] text-ink-3">{i18n._(hint)}</span>
              </span>
            </label>
          ))}
        </RadioGroup>
      </fieldset>

      {/* Negotiated seat price. Only meaningful on a licence contract. */}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="org-seat-price-override" className="font-medium text-[12px] text-ink-2">
          <Trans>Prezzo per utente negoziato</Trans>
        </label>
        <div className="flex items-center gap-2">
          <input
            id="org-seat-price-override"
            type="number"
            min={0}
            step="0.01"
            inputMode="decimal"
            value={priceDraft}
            disabled={update.isPending}
            onChange={(e) => setPriceDraft(e.target.value)}
            onBlur={commitPrice}
            placeholder={i18n._(msg`Prezzo di listino del tier`)}
            aria-describedby="org-seat-price-override-hint"
            className="w-40 rounded-md border border-hairline bg-card px-2.5 py-1.5 text-[12.5px] text-ink-1 tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-accent"
          />
          <span className="text-[12px] text-ink-3">
            <Trans>€ / utente / mese</Trans>
          </span>
        </div>
        <p id="org-seat-price-override-hint" className="text-[11.5px] text-ink-3">
          <Trans>
            Vuoto usa il prezzo del tier. Un valore qui vale solo per questa organizzazione e si
            applica ai nuovi abbonamenti: quelli già attivi mantengono il prezzo con cui sono
            partiti.
          </Trans>
        </p>
      </div>
    </section>
  );
}
